import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  HiOutlineMail,
  HiOutlineLockClosed,
  HiOutlineEye,
  HiOutlineEyeOff,
} from 'react-icons/hi';
import { authApi } from '../../services/api';

const RESEND_COOLDOWN_SEC = 60;

export default function ForgotPasswordForm({ initialEmail = '', onSuccess }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(initialEmail);
  const [resetToken, setResetToken] = useState(null);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldownLeft, setCooldownLeft] = useState(0);

  useEffect(() => {
    setEmail(initialEmail || '');
  }, [initialEmail]);

  useEffect(() => {
    if (cooldownLeft <= 0) return undefined;
    const t = setInterval(() => {
      setCooldownLeft((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [cooldownLeft]);

  const startCooldown = useCallback(() => {
    setCooldownLeft(RESEND_COOLDOWN_SEC);
  }, []);

  const resetForm = () => {
    setStep(1);
    setResetToken(null);
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setCooldownLeft(0);
  };

  const requestOtp = async (e) => {
    e?.preventDefault();
    if (!email.trim()) {
      toast.error('Enter your email');
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.forgotPasswordRequest({ email: email.trim() });
      toast.success(res.data.message || 'Check your email');
      if (res.data.reset_token) {
        setResetToken(res.data.reset_token);
        setStep((s) => (s === 1 ? 2 : s));
        setOtp('');
        startCooldown();
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Request failed';
      if (error.response?.status === 429) {
        toast.error(msg);
        const retry = error.response?.data?.retry_after_seconds;
        if (retry) setCooldownLeft(Number(retry) || RESEND_COOLDOWN_SEC);
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp.trim())) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      await authApi.forgotPasswordVerifyOtp({
        reset_token: resetToken,
        otp: otp.trim(),
      });
      toast.success('Code verified');
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await authApi.forgotPasswordReset({
        reset_token: resetToken,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      toast.success('Password updated successfully');
      resetForm();
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  if (step === 1) {
    return (
      <form onSubmit={requestOtp} className="space-y-3">
        <p className="text-xs text-neutral-500">
          We will send a one-time code to your email if an account exists.
        </p>
        <div>
          <label className="block text-[10px] font-semibold text-neutral-500 uppercase mb-1">Email</label>
          <div className="relative">
            <HiOutlineMail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-neutral-200 bg-white pl-9 pr-3 py-2 text-sm focus:ring-1 focus:ring-primary-500 outline-none"
              placeholder="you@example.com"
              required
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={loading || cooldownLeft > 0}
          className="px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
        >
          {loading ? 'Sending…' : cooldownLeft > 0 ? `Wait ${cooldownLeft}s` : 'Send code'}
        </button>
      </form>
    );
  }

  if (step === 2) {
    return (
      <form onSubmit={verifyOtp} className="space-y-3">
        <p className="text-xs text-neutral-500">
          Enter the 6-digit code sent to {email.trim()}.
        </p>
        <div>
          <label className="block text-[10px] font-semibold text-neutral-500 uppercase mb-1">Verification code</label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full rounded-md border border-neutral-200 px-3 py-2 text-center text-lg font-mono tracking-[0.3em] focus:ring-1 focus:ring-primary-500 outline-none"
            placeholder="000000"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="submit"
            disabled={loading || otp.length !== 6}
            className="px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? 'Verifying…' : 'Verify code'}
          </button>
          <button
            type="button"
            onClick={requestOtp}
            disabled={loading || cooldownLeft > 0}
            className="text-sm text-primary-600 hover:underline disabled:opacity-50"
          >
            {cooldownLeft > 0 ? `Resend in ${cooldownLeft}s` : 'Resend code'}
          </button>
          <button
            type="button"
            onClick={resetForm}
            className="text-sm text-neutral-500 hover:text-neutral-700"
          >
            Start over
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={submitNewPassword} className="space-y-3">
      <p className="text-xs text-neutral-500">Use at least 8 characters.</p>
      <div>
        <label className="block text-[10px] font-semibold text-neutral-500 uppercase mb-1">New password</label>
        <div className="relative">
          <HiOutlineLockClosed className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            type={showNewPass ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full rounded-md border border-neutral-200 bg-white pl-9 pr-10 py-2 text-sm focus:ring-1 focus:ring-primary-500 outline-none"
            placeholder="At least 8 characters"
            minLength={8}
            required
          />
          <button
            type="button"
            onClick={() => setShowNewPass(!showNewPass)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-neutral-400 hover:text-neutral-600"
          >
            {showNewPass ? <HiOutlineEyeOff className="w-4 h-4" /> : <HiOutlineEye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div>
        <label className="block text-[10px] font-semibold text-neutral-500 uppercase mb-1">Confirm password</label>
        <div className="relative">
          <HiOutlineLockClosed className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            type={showNewPass ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full rounded-md border border-neutral-200 bg-white pl-9 pr-3 py-2 text-sm focus:ring-1 focus:ring-primary-500 outline-none"
            placeholder="Repeat password"
            minLength={8}
            required
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
        >
          {loading ? 'Saving…' : 'Update password'}
        </button>
        <button
          type="button"
          onClick={resetForm}
          className="text-sm text-neutral-500 hover:text-neutral-700"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
